import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";

import { decrypt } from "@/lib/crypto";
import { getFluxxUserId } from "@/services/postLoginUser";

import type { EffectiveRole } from "../auth/types";
import type { RoleFeature } from "../services/roles";
import { getUserDetails } from "../services/roles";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

type RoleFeaturesGroup = {
  role_code: string;
  features: RoleFeature[];
};

type RoleState = {
  roleFeatures: RoleFeaturesGroup[];
  selectedRole: string | null;
  fluxxUserId: string | null;
  features: RoleFeature[];
  language: string;
  showTour: boolean;
  tourReason?: string;
  isAdmin: boolean;
  isOperator: boolean;
  accessibilityOpen: boolean;
  sessionTimeoutOpen: boolean;
  status: RequestStatus;
  error: string | null;
};

const initialState: RoleState = {
  roleFeatures: [],
  selectedRole: null,
  fluxxUserId: null,
  features: [],
  language: "",
  showTour: false,
  tourReason: undefined,
  isAdmin: false,
  isOperator: false,
  accessibilityOpen: false,
  sessionTimeoutOpen: false,
  status: "idle",
  error: null,
};

export const fetchUserDetailsByEmail = createAsyncThunk<
  {
    roleFeatures: RoleFeaturesGroup[];
    showTour: boolean;
    reason?: string;
    language: string;
    isAdmin: boolean;
    isOperator: boolean;
  },
  string,
  { state: any }
>("role/fetchUserDetailsByEmail", async (email) => {
  const { data } = await getUserDetails(email);

  return {
    roleFeatures: Array.isArray(data?.roleFeatures) ? data.roleFeatures : [],
    showTour: Boolean(data?.showTour),
    reason: data?.reason,
    language: data?.language,
    isAdmin: Boolean(data?.isAdmin),
    isOperator: Boolean(data?.isOperator),
  };
});

export const getFluxxId: any = createAsyncThunk<string, string>(
  "role/getFluxxUserId",
  async (userId: string) => {
    const response: any = await getFluxxUserId(userId);
    return decrypt(response?.e || response?.userId) || "";
  }
);

export const roleSlice = createSlice({
  name: "role",
  initialState,

  reducers: {
    setShowTour(state, action: PayloadAction<boolean>) {
      state.showTour = action.payload;
    },

    setSelectedRole(state, action: PayloadAction<string>) {
      const role = action.payload;

      state.selectedRole = role;

      const selectedGroup = state.roleFeatures.find((r) => r.role_code === role);

      state.features = selectedGroup?.features ?? [];

      localStorage.setItem("selectedRole", role);
    },

    setLanguage(state, action: PayloadAction<string>) {
      state.language = action.payload;
    },

    setAccessibilityOpen(state, action: PayloadAction<boolean>) {
      state.accessibilityOpen = action.payload;
    },

    setSessionTimeoutOpen(state, action: PayloadAction<boolean>) {
      state.sessionTimeoutOpen = action.payload;
    },

    resetRoleState(state) {
      state.features = [];
      state.roleFeatures = [];
      state.selectedRole = null;
      state.language = "";
      state.showTour = false;
      state.tourReason = undefined;
      state.isAdmin = false;
      state.accessibilityOpen = false;
      state.sessionTimeoutOpen = false;
      state.status = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsByEmail.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })

      .addCase(fetchUserDetailsByEmail.fulfilled, (state, action) => {
        state.status = "succeeded";

        state.roleFeatures = action.payload.roleFeatures;

        const availableRoles = action.payload.roleFeatures.map((r) => r.role_code);

        const persistedRole = (localStorage.getItem("selectedRole") as EffectiveRole) || "";

        const validPersistedRole =
          persistedRole && availableRoles.includes(persistedRole) ? persistedRole : null;

        const finalRole = validPersistedRole ?? availableRoles[0] ?? null;

        state.selectedRole = finalRole;

        const selectedGroup = state.roleFeatures.find((r) => r.role_code === finalRole);

        state.features = selectedGroup?.features ?? [];

        state.language = action.payload.language || "English";
        state.showTour = action.payload.showTour;
        state.tourReason = action.payload.reason;
        state.isAdmin = action.payload.isAdmin;
        state.isOperator = action.payload.isOperator;
      })

      .addCase(fetchUserDetailsByEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load user profile";
        state.features = [];
      })

      .addCase(getFluxxId.fulfilled, (state, action) => {
        state.fluxxUserId = action.payload;
      });
  },
});

export const {
  setSelectedRole,
  resetRoleState,
  setShowTour,
  setLanguage,
  setAccessibilityOpen,
  setSessionTimeoutOpen,
} = roleSlice.actions;

export const selectFluxxUserId = (s: any): string | null => s.role.fluxxUserId ?? null;

export const selectRoles = createSelector([(s: any) => s.role.roleFeatures], (roleFeatures) =>
  roleFeatures.map((group: RoleFeaturesGroup) => group.role_code as EffectiveRole)
);

export const selectFeatures = (s: any): RoleFeature[] => s.role.features;

export const selectLanguage = (s: any): string => s.role.language;

export const selectShowTour = (s: any): boolean => s.role.showTour;

export const selectIsAdmin = (s: any): boolean => s.role.isAdmin;

export const selectIsOperator = (s: any): boolean => s.role.isOperator;

export const selectTourReason = (s: any): string | undefined => s.role.tourReason;

export const selectFeaturesStatus = (s: any): RequestStatus => s.role.status;

export const selectFeaturesLoading = (s: any): boolean => s.role.status === "loading";

export const selectFeaturesError = (s: any): string | null => s.role.error;

export const selectSelectedRole = (s: any): string | null => s.role.selectedRole;

export const selectAccessibilityOpen = (s: any): boolean => s.role.accessibilityOpen;
export const selectSessionTimeoutOpen = (s: any): boolean => s.role.sessionTimeoutOpen;

export const selectHasFeature = (state: any, featureCode: string): boolean => {
  const normalized = featureCode?.toLowerCase();
  return state.role.features.some((f: RoleFeature) => f.feature_code?.toLowerCase() === normalized);
};

export const selectHasFeatureByPath = (state: any, routePath: string): boolean => {
  const normalized = routePath?.toLowerCase();
  return state.role.features.some(
    (f: RoleFeature) => f.route_path?.toLowerCase() === normalized && f.access
  );
};

export const selectDashboardFeatures = (state: any): RoleFeature[] =>
  state.role.features.filter(
    (f: RoleFeature) =>
      f.domain === "dashboard" &&
      f.feature_code !== "dashboard" &&
      f.feature_code !== "dashboard.settings" &&
      f.access
  );

export default roleSlice.reducer;
